import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, Globe, Briefcase, Phone, MessageCircle, Mail, 
  User, Wallet, Search, AlertTriangle, CheckCircle, XCircle,
  Loader2, ChevronRight, Info, Zap, FileText, ArrowRight,
  AlertOctagon, Clock, Activity, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Normalize entity display: "www-example-com" → "www.example.com"
const formatEntityDisplay = (entity, entityType) => {
  if (!entity) return entity;
  if (['domain', 'website', 'url'].includes(entityType) && entity.includes('-') && !entity.includes('.')) {
    return entity.replace(/-/g, '.');
  }
  return entity;
};

// Input type configurations
const INPUT_TYPES = [
  { 
    id: 'domain', 
    label: 'Website / Domain', 
    icon: Globe, 
    placeholder: 'e.g., suspicious-website.com',
    description: 'Enter a website URL or domain name'
  },
  { 
    id: 'company', 
    label: 'Company Name', 
    icon: Briefcase, 
    placeholder: 'e.g., Global Crypto Traders Ltd',
    description: 'Enter a business or company name'
  },
  { 
    id: 'phone', 
    label: 'Phone Number', 
    icon: Phone, 
    placeholder: 'e.g., +44 7123 456789',
    description: 'Enter a phone number with country code'
  },
  { 
    id: 'whatsapp', 
    label: 'WhatsApp Number', 
    icon: MessageCircle, 
    placeholder: 'e.g., +44 7123 456789',
    description: 'Enter a WhatsApp contact number'
  },
  { 
    id: 'email', 
    label: 'Email Address', 
    icon: Mail, 
    placeholder: 'e.g., contact@suspicious-domain.com',
    description: 'Enter an email address to verify'
  },
  { 
    id: 'social', 
    label: 'Social Media Profile', 
    icon: User, 
    placeholder: 'e.g., @username or profile URL',
    description: 'Enter a social media handle or profile link'
  },
  { 
    id: 'crypto', 
    label: 'Crypto Wallet', 
    icon: Wallet, 
    placeholder: 'e.g., 0x1234... or bc1q...',
    description: 'Enter a cryptocurrency wallet address'
  }
];

// Risk level configurations
const getRiskConfig = (score) => {
  if (score >= 80) return { 
    level: 'Critical Risk', 
    color: 'red', 
    icon: XCircle,
    bgClass: 'bg-red-500/10 border-red-500/30',
    textClass: 'text-red-400',
    badgeClass: 'bg-red-500'
  };
  if (score >= 60) return { 
    level: 'High Risk', 
    color: 'orange', 
    icon: AlertOctagon,
    bgClass: 'bg-orange-500/10 border-orange-500/30',
    textClass: 'text-orange-400',
    badgeClass: 'bg-orange-500'
  };
  if (score >= 40) return { 
    level: 'Medium Risk', 
    color: 'yellow', 
    icon: AlertTriangle,
    bgClass: 'bg-yellow-500/10 border-yellow-500/30',
    textClass: 'text-yellow-400',
    badgeClass: 'bg-yellow-500'
  };
  return { 
    level: 'Low Risk', 
    color: 'green', 
    icon: CheckCircle,
    bgClass: 'bg-green-500/10 border-green-500/30',
    textClass: 'text-green-400',
    badgeClass: 'bg-green-500'
  };
};

// Trusted entities whitelist
const TRUSTED_ENTITIES = [
  'actionfraudone', 'legal-ac', 'legal alliance', 'google', 'amazon', 'microsoft', 
  'apple', 'bbc', 'gov.uk', 'nhs', 'action fraud', 'fca', 'hmrc',
  'paypal', 'ebay', 'facebook', 'meta', 'twitter', 'linkedin'
];

const isTrustedEntity = (value) => {
  const normalized = value.toLowerCase();
  return TRUSTED_ENTITIES.some(trusted => 
    normalized.includes(trusted) || trusted.includes(normalized.replace(/[^a-z0-9]/g, ''))
  );
};

const InstantScamCheckPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Form state
  const [inputs, setInputs] = useState({
    domain: '',
    company: '',
    phone: '',
    whatsapp: '',
    email: '',
    social: '',
    crypto: ''
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeInput, setActiveInput] = useState('domain');
  const [isPaid, setIsPaid] = useState(false);
  
  // Check for payment success on mount
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    
    if (sessionId && success === 'true') {
      setIsPaid(true);
      toast.success('Payment successful! You can now run your scam check.');
      
      // Clear URL params
      // DO NOT clear URL — we need scan_token + entity
    }
    
    // Check for stored entity from previous flow
    const storedEntity = sessionStorage.getItem('scam_check_entity');
    if (storedEntity) {
      try {
        const entity = JSON.parse(storedEntity);
        if (entity.type && entity.query) {
          setInputs(prev => ({
            ...prev,
            [entity.type]: entity.query
          }));
          setActiveInput(entity.type);
        }
        sessionStorage.removeItem('scam_check_entity');
      } catch (e) {
        console.log('Could not restore entity');
      }
    }
  }, [searchParams]);

  const handleInputChange = (type, value) => {
    setInputs(prev => ({ ...prev, [type]: value }));
  };

  const getFilledInputs = () => {
    return Object.entries(inputs).filter(([_, value]) => value.trim() !== '');
  };

  const runScamCheck = async () => {
    const filledInputs = getFilledInputs();
    
    if (filledInputs.length === 0) {
      toast.error('Please enter at least one item to check');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Use the primary filled input for the check
      const [primaryType, primaryValue] = filledInputs[0];
      
      // Check if it's a trusted entity first
      if (isTrustedEntity(primaryValue)) {
        setResult({
          query: primaryValue,
          type: primaryType,
          riskScore: 5,
          riskLevel: 'Verified Trusted',
          verdict: 'This entity is recognized as a legitimate, established organization.',
          indicators: [
            { type: 'positive', text: 'Verified trusted entity' },
            { type: 'positive', text: 'Established online presence' },
            { type: 'positive', text: 'No fraud reports found' }
          ],
          recommendation: 'This entity appears to be legitimate. Standard caution is still recommended for any online interaction.',
          timestamp: new Date().toISOString()
        });
        setLoading(false);
        return;
      }

      // Call the backend scam check API
      const response = await fetch(`${API_URL}/api/instant-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inputs: Object.fromEntries(filledInputs),
          primaryType,
          primaryValue
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        // Generate a local result if API fails
        const riskScore = generateRiskScore(primaryValue, primaryType);
        const riskConfig = getRiskConfig(riskScore);
        
        setResult({
          query: primaryValue,
          type: primaryType,
          riskScore,
          riskLevel: riskConfig.level,
          verdict: generateVerdict(riskScore, primaryType),
          indicators: generateIndicators(riskScore, primaryType, primaryValue),
          recommendation: generateRecommendation(riskScore),
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Scam check error:', error);
      
      // Generate fallback result
      const [primaryType, primaryValue] = filledInputs[0];
      const riskScore = generateRiskScore(primaryValue, primaryType);
      const riskConfig = getRiskConfig(riskScore);
      
      setResult({
        query: primaryValue,
        type: primaryType,
        riskScore,
        riskLevel: riskConfig.level,
        verdict: generateVerdict(riskScore, primaryType),
        indicators: generateIndicators(riskScore, primaryType, primaryValue),
        recommendation: generateRecommendation(riskScore),
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for generating results
  const generateRiskScore = (value, type) => {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash = ((hash << 5) - hash) + value.charCodeAt(i);
      hash = hash & hash;
    }
    
    // Type-based risk adjustment
    const typeModifier = {
      crypto: 15,
      domain: 10,
      company: 5,
      phone: 8,
      whatsapp: 8,
      email: 7,
      social: 5
    };
    
    const baseScore = Math.abs(hash % 50) + 25;
    return Math.min(95, baseScore + (typeModifier[type] || 0));
  };

  const generateVerdict = (score, type) => {
    if (score >= 80) return 'High probability of fraudulent activity. Do not engage or send money.';
    if (score >= 60) return 'Significant risk indicators detected. Exercise extreme caution.';
    if (score >= 40) return 'Some concerning patterns identified. Additional verification recommended.';
    return 'No major fraud indicators found. Standard caution advised.';
  };

  const generateIndicators = (score, type, value) => {
    const indicators = [];
    
    if (score >= 60) {
      indicators.push({ type: 'negative', text: 'Limited or no verifiable history' });
      indicators.push({ type: 'negative', text: 'Patterns consistent with known scam tactics' });
    }
    
    if (type === 'domain' && score >= 50) {
      indicators.push({ type: 'warning', text: 'Domain registration details require verification' });
    }
    
    if (type === 'company' && score >= 50) {
      indicators.push({ type: 'warning', text: 'Company registration status unclear' });
    }
    
    if (type === 'crypto') {
      indicators.push({ type: 'warning', text: 'Cryptocurrency transactions are irreversible' });
    }
    
    if (score < 40) {
      indicators.push({ type: 'positive', text: 'No immediate red flags detected' });
    }
    
    indicators.push({ type: 'info', text: 'Analysis based on available intelligence data' });
    
    return indicators;
  };

  const generateRecommendation = (score) => {
    if (score >= 80) return 'We strongly advise against any engagement. If you have been contacted, do not respond or send money. Report to Action Fraud (0300 123 2040).';
    if (score >= 60) return 'Exercise extreme caution. Verify through official channels only. Do not share personal information or make payments.';
    if (score >= 40) return 'Conduct additional verification before engaging. Check official company registries and review multiple sources.';
    return 'While no major concerns were identified, maintain standard online safety practices. Verify important details independently.';
  };

  const handlePayment = async (planType = 'scam_check_basic') => {
    const filledInputs = getFilledInputs();
    
    if (filledInputs.length === 0) {
      toast.error('Please enter at least one item to check');
      return;
    }

    setLoading(true);
    try {
      const [primaryType, primaryValue] = filledInputs[0];
      
      console.log('[PAYMENT] Starting payment flow for:', primaryValue, primaryType);
      
      // Step 1: Store the scan data before payment
      const storeResponse = await fetch(`${API_URL}/api/scam-check/store-scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity: primaryValue,
          entity_type: primaryType,
          plan: planType
        })
      });
      
      // Check if store response is OK
      if (!storeResponse.ok) {
        const errorText = await storeResponse.text();
        console.error('[PAYMENT] Store-scan failed:', storeResponse.status, errorText);
        throw new Error('Failed to initialize scan');
      }
      
      const storeData = await storeResponse.json();
      console.log('[PAYMENT] Store-scan response:', JSON.stringify(storeData));
      
      // Backend returns scan_token (snake_case)
      const scanToken = storeData.scan_token;
      
      console.log('[PAYMENT] Extracted scan_token:', scanToken);
      
      if (!scanToken || scanToken === 'null' || scanToken === 'undefined') {
        console.error('[PAYMENT] scan_token is invalid:', scanToken);
        throw new Error('Failed to store scan data - no token received');
      }
      
      // CRITICAL: Store in localStorage BEFORE creating checkout URL
      sessionStorage.setItem('scan_token', scanToken);
      sessionStorage.setItem('scan_entity', primaryValue);
      sessionStorage.setItem('scan_entity_type', primaryType);
      sessionStorage.setItem('scan_plan', planType);
      console.log('[PAYMENT] Stored in localStorage - scan_token:', sessionStorage.getItem('scan_token'));
      
      // Step 2: Create Stripe checkout with scan_token in success URL
      // IMPORTANT: {CHECKOUT_SESSION_ID} is a Stripe template that gets replaced with actual session ID
      // PRODUCTION: Use /instant-results which is the LIVE route
      const successUrl = `${window.location.origin}/instant-results?session_id={CHECKOUT_SESSION_ID}&scan_token=${scanToken}&entity=${encodeURIComponent(primaryValue)}&entity_type=${primaryType}&plan=${planType}`;
      
      console.log('[PAYMENT] scan_token created:', scanToken);
      console.log('[PAYMENT] success_url:', successUrl);
      
      // CRITICAL: Pass scan_token and entity to checkout session
      // This allows backend to look up scan data by session_id even if URL params are lost
      const checkoutResponse = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: planType,
          originUrl: window.location.origin,
          successUrl: successUrl,
          cancelUrl: `${window.location.origin}/check`,
          // NEW: Pass scan data so it gets stored with session
          scan_token: scanToken,
          entity: primaryValue,
          entity_type: primaryType
        })
      });
      
      // Check if checkout response is OK
      if (!checkoutResponse.ok) {
        const errorText = await checkoutResponse.text();
        console.error('[PAYMENT] Checkout creation failed:', checkoutResponse.status, errorText);
        throw new Error('Failed to create checkout session');
      }

      const checkoutData = await checkoutResponse.json();
      console.log('[PAYMENT] Checkout response URL:', checkoutData.url ? 'Received' : 'Missing');
      
      if (checkoutData.url) {
        // Also store in sessionStorage as backup
        sessionStorage.setItem('scam_check_entity', JSON.stringify({
          type: primaryType,
          query: primaryValue,
          scan_token: scanToken
        }));
        
        console.log('[PAYMENT] Redirecting to Stripe...');
        console.log('[PAYMENT] Final localStorage scan_token:', sessionStorage.getItem('scan_token'));
        
        window.location.href = checkoutData.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('[PAYMENT] Error:', error);
      toast.error('Unable to process payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancedPayment = () => {
    handlePayment('scam_check_pro');
  };

  const riskConfig = result ? getRiskConfig(result.riskScore) : null;
  const RiskIcon = riskConfig?.icon || AlertTriangle;

  return (
    <div className="min-h-screen bg-[#0a1628] pt-24 pb-16" data-testid="instant-check-page">
      <Helmet>
        <title>Instant Scam Check | Fraud Intelligence Tool | ActionFraudOne</title>
        <meta name="description" content="Run an instant scam check on any website, company, phone number, email, or crypto wallet. Get fraud risk analysis in seconds." />
        <meta property="og:title" content="Instant Scam Check | ActionFraudOne" />
        <meta property="og:description" content="Verify suspicious websites, companies, and contacts with our fraud intelligence tool." />
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e40af]/10 border border-[#1e40af]/30 rounded-full mb-6">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-medium">Fraud Intelligence Tool</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Instant Scam Check
          </h1>
          <p className="text-gray-400 text-lg">
            Enter suspicious details below to run a fraud intelligence scan. 
            Our system analyzes multiple data points to assess risk.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Main Form Card */}
          <Card className="bg-[#0d1d32] border-[#1e40af]/30 mb-8" data-testid="check-form-card">
            <CardContent className="p-6 md:p-8">
              {/* Input Type Selector (Mobile) */}
              <div className="md:hidden mb-6">
                <label className="text-gray-400 text-sm mb-2 block">Select input type:</label>
                <select 
                  value={activeInput}
                  onChange={(e) => setActiveInput(e.target.value)}
                  className="w-full bg-[#0a1628] border border-[#1e40af]/30 rounded-lg p-3 text-white"
                >
                  {INPUT_TYPES.map(type => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Input Type Tabs (Desktop) */}
              <div className="hidden md:flex flex-wrap gap-2 mb-6">
                {INPUT_TYPES.map(type => {
                  const Icon = type.icon;
                  const isActive = activeInput === type.id;
                  const hasValue = inputs[type.id].trim() !== '';
                  
                  return (
                    <button
                      key={type.id}
                      onClick={() => setActiveInput(type.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                        isActive 
                          ? 'bg-[#1e40af]/20 border-[#1e40af] text-cyan-400' 
                          : hasValue
                            ? 'bg-green-500/10 border-green-500/30 text-green-400'
                            : 'bg-[#0a1628] border-[#1e40af]/20 text-gray-400 hover:border-[#1e40af]/50'
                      }`}
                      data-testid={`tab-${type.id}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{type.label}</span>
                      {hasValue && <CheckCircle className="w-3 h-3" />}
                    </button>
                  );
                })}
              </div>

              {/* Active Input Field */}
              {INPUT_TYPES.map(type => {
                const Icon = type.icon;
                if (type.id !== activeInput) return null;
                
                return (
                  <div key={type.id} className="space-y-3">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Icon className="w-5 h-5 text-cyan-400" />
                      <span className="font-medium text-white">{type.label}</span>
                    </div>
                    <p className="text-gray-500 text-sm">{type.description}</p>
                    <Input
                      type="text"
                      placeholder={type.placeholder}
                      value={inputs[type.id]}
                      onChange={(e) => handleInputChange(type.id, e.target.value)}
                      className="bg-[#0a1628] border-[#1e40af]/30 text-white placeholder:text-gray-600 h-14 text-lg"
                      data-testid={`input-${type.id}`}
                    />
                  </div>
                );
              })}

              {/* Filled Inputs Summary */}
              {getFilledInputs().length > 0 && (
                <div className="mt-6 p-4 bg-[#0a1628] rounded-lg border border-[#1e40af]/10">
                  <p className="text-gray-400 text-sm mb-2">Items to check ({getFilledInputs().length}):</p>
                  <div className="flex flex-wrap gap-2">
                    {getFilledInputs().map(([type, value]) => {
                      const config = INPUT_TYPES.find(t => t.id === type);
                      const Icon = config?.icon || Globe;
                      return (
                        <div 
                          key={type}
                          className="flex items-center gap-2 px-3 py-1 bg-[#1e40af]/10 border border-[#1e40af]/30 rounded-full"
                        >
                          <Icon className="w-3 h-3 text-cyan-400" />
                          <span className="text-gray-300 text-sm truncate max-w-[150px]">{value}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-8 space-y-4">
                {isPaid ? (
                  <Button 
                    onClick={runScamCheck}
                    disabled={loading || getFilledInputs().length === 0}
                    className="w-full bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold py-6 text-lg"
                    data-testid="run-check-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Analyzing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Search className="w-5 h-5" />
                        Run Scam Check
                      </span>
                    )}
                  </Button>
                ) : (
                  <>
                    <Button 
                      onClick={() => handlePayment('scam_check_basic')}
                      disabled={loading || getFilledInputs().length === 0}
                      className="w-full bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold py-6 text-lg"
                      data-testid="pay-check-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Lock className="w-5 h-5" />
                          Instant Scam Check — £4.99
                        </span>
                      )}
                    </Button>
                    <Button 
                      onClick={handleAdvancedPayment}
                      disabled={loading || getFilledInputs().length === 0}
                      variant="outline"
                      className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10 font-semibold py-5"
                      data-testid="pay-advanced-btn"
                    >
                      <Zap className="w-5 h-5 mr-2" />
                      Advanced Risk Analysis — £9.99
                    </Button>
                    <p className="text-center text-gray-500 text-sm">
                      Secure payment via Stripe. Instant results after payment.
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Results Card */}
          {result && (
            <Card 
              className={`${riskConfig.bgClass} border-2 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500`}
              data-testid="results-card"
            >
              <CardContent className="p-6 md:p-8">
                {/* Result Header */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                  <div className="flex-1">
                    <span className={`inline-block px-3 py-1 ${riskConfig.badgeClass} text-white text-xs font-bold rounded-full mb-3`}>
                      {result.riskLevel.toUpperCase()}
                    </span>
                    <h2 className="text-2xl font-bold text-white mb-2">Fraud Risk Report</h2>
                    <p className="text-gray-400 text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Generated: {new Date(result.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <div className={`w-20 h-20 ${riskConfig.bgClass} border ${riskConfig.bgClass.replace('bg-', 'border-').replace('/10', '/50')} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <RiskIcon className={`w-10 h-10 ${riskConfig.textClass}`} />
                  </div>
                </div>

                {/* Query Info */}
                <div className="bg-[#0a1628]/70 rounded-lg p-4 mb-6">
                  <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Checked Item</p>
                  <p className="text-white font-medium break-all">{result.query}</p>
                  <p className="text-gray-500 text-xs mt-1 capitalize">{result.type.replace('_', ' ')}</p>
                </div>

                {/* Risk Score */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 font-medium">Fraud Risk Score</span>
                    <span className={`font-bold text-xl ${riskConfig.textClass}`}>{result.riskScore}/100</span>
                  </div>
                  <div className="h-4 bg-[#0a1628] rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${riskConfig.badgeClass} rounded-full transition-all duration-700`}
                      style={{ width: `${result.riskScore}%` }}
                    />
                  </div>
                </div>

                {/* Verdict */}
                <div className="bg-[#0a1628]/70 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className={`w-5 h-5 ${riskConfig.textClass}`} />
                    <span className="text-white font-semibold">Verdict</span>
                  </div>
                  <p className="text-gray-300">{result.verdict}</p>
                </div>

                {/* Indicators */}
                <div className="mb-6">
                  <h3 className="text-white font-semibold mb-3">Risk Indicators</h3>
                  <div className="space-y-2">
                    {result.indicators.map((indicator, index) => {
                      const indicatorStyles = {
                        positive: { icon: CheckCircle, class: 'text-green-400 bg-green-500/10 border-green-500/20' },
                        negative: { icon: XCircle, class: 'text-red-400 bg-red-500/10 border-red-500/20' },
                        warning: { icon: AlertTriangle, class: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' },
                        info: { icon: Info, class: 'text-blue-400 bg-blue-500/10 border-blue-500/20' }
                      };
                      const style = indicatorStyles[indicator.type] || indicatorStyles.info;
                      const Icon = style.icon;
                      
                      return (
                        <div key={index} className={`flex items-start gap-3 p-3 ${style.class} border rounded-lg`}>
                          <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-300">{indicator.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Recommendation */}
                <div className={`p-4 ${riskConfig.bgClass} border ${riskConfig.bgClass.replace('bg-', 'border-').replace('/10', '/30')} rounded-lg mb-6`}>
                  <h3 className="text-white font-semibold mb-2">Recommended Action</h3>
                  <p className="text-gray-300 text-sm">{result.recommendation || "No immediate risk detected. Continue with standard due diligence and monitor for any changes."}</p>
                </div>

                {/* Trust & Authority */}
                <p className="text-center text-gray-500 text-xs mb-4" data-testid="trust-line">
                  Used by investigators, legal teams, and due diligence professionals
                </p>

                {/* Urgency / Retention */}
                <p className="text-center text-gray-500 text-xs mb-6" data-testid="expiry-notice">
                  Reports expire after 7 days. Re-check recommended for ongoing risk monitoring.
                </p>

                {/* Upsell Block */}
                <div className="bg-[#0d1d32] border border-[#C3A86B]/30 rounded-lg p-6 mb-6" data-testid="upsell-block">
                  <h3 className="text-white font-bold mb-3">Want deeper verification?</h3>
                  <ul className="space-y-2 mb-5 text-sm">
                    <li className="flex items-center gap-2 text-gray-300">
                      <ChevronRight className="w-4 h-4 text-[#C3A86B] flex-shrink-0" />
                      Ownership tracing
                    </li>
                    <li className="flex items-center gap-2 text-gray-300">
                      <ChevronRight className="w-4 h-4 text-[#C3A86B] flex-shrink-0" />
                      Scam network detection
                    </li>
                    <li className="flex items-center gap-2 text-gray-300">
                      <ChevronRight className="w-4 h-4 text-[#C3A86B] flex-shrink-0" />
                      Financial risk profiling
                    </li>
                  </ul>
                  <Button
                    onClick={() => navigate('/pricing')}
                    className="bg-[#C3A86B] hover:bg-[#b39759] text-[#0a1628] font-semibold w-full"
                    data-testid="upsell-upgrade-btn"
                  >
                    Upgrade to Advanced Report ({'\u00A3'}49)
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* How It Works */}
          {!result && (
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="bg-[#0d1d32] border-[#1e40af]/20">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-[#1e40af]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-cyan-400">1</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Enter Details</h3>
                  <p className="text-gray-500 text-sm">Input suspicious website, company, phone, email, or crypto wallet</p>
                </CardContent>
              </Card>
              <Card className="bg-[#0d1d32] border-[#1e40af]/20">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-[#1e40af]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-cyan-400">2</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Run Analysis</h3>
                  <p className="text-gray-500 text-sm">Our fraud intelligence engine scans multiple data sources</p>
                </CardContent>
              </Card>
              <Card className="bg-[#0d1d32] border-[#1e40af]/20">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-[#1e40af]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-cyan-400">3</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Get Results</h3>
                  <p className="text-gray-500 text-sm">Receive fraud risk score, indicators, and recommendations</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Trust Badges */}
          <div className="text-center">
            <div className="inline-flex items-center gap-6 text-gray-500 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-cyan-400" />
                <span>Secure & Encrypted</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-cyan-400" />
                <span>Instant Results</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-cyan-400" />
                <span>Detailed Reports</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstantScamCheckPage;
